import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Box,
  Alert,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/api';

interface ChannelStats {
  received: number;
  commented: number;
  filtered: number;
  failed: number;
}

interface AccountStats {
  comments_sent: number;
  failed: number;
  blocked_count?: number;
}

interface LastComment {
  channel: string;
  account: string;
  timestamp: string;
  comment_text: string;        // ← ВАЖНО! именно так приходит
  original_text: string;       // ← оригинальный пост
}

interface LastError {
  message: string;
  timestamp: string;
  channel?: string;
  account?: string;
}

interface NeuroCommentatorStats {
  task_id: string;
  accounts_count: number;
  total_messages_received: number;
  total_comments_sent: number;
  total_messages_filtered: number;
  total_comments_failed: number;
  channel_stats: Record<string, ChannelStats>;
  account_stats: Record<string, AccountStats>;
  last_successful_comment: LastComment | null;
  last_error: LastError | null;
  started_at: string;
  last_activity_at?: string | null;
  uptime_seconds: number;
}

const NeuroCommentingDetail: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();

  const [stats, setStats] = useState<NeuroCommentatorStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = async () => {
    try {
      const res = await api.get(`/neuro_commenting/status/${taskId}`);
      setStats(res.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Задача не найдена');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!taskId) return;
    fetchStats();
    const interval = setInterval(fetchStats, 8000);
    return () => clearInterval(interval);
  }, [taskId]);

  if (loading) {
    return (
      <Container sx={{ py: 10, textAlign: 'center' }}>
        <CircularProgress size={70} />
        <Typography variant="h6" mt={3}>Загрузка статистики...</Typography>
      </Container>
    );
  }

  if (error || !stats) {
    return (
      <Container sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Задача не найдена или была удалена'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/neuro_commenting')}>
          Назад к нейрокомментингу
        </Button>
      </Container>
    );
  }

  const uptime = Math.floor(stats.uptime_seconds);
  const hours = Math.floor(uptime / 3600);
  const minutes = Math.floor((uptime % 3600) / 60);
  const seconds = uptime % 60;

  const lastComment = stats.last_successful_comment;

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/neuro_commenting')}
        sx={{ mb: 4 }}
      >
        Назад к нейрокомментингу
      </Button>

      <Paper elevation={4} sx={{ p: 5, borderRadius: 3, bgcolor: '#F8FAFC' }}>
        <Typography variant="h4" fontWeight="bold" color="#1E3A8A" gutterBottom>
          Нейрокомментатор #{taskId?.slice(0, 8)}
        </Typography>
        <Typography color="#4B5563" fontSize="1.1rem">
          Аккаунтов: <strong>{stats.accounts_count}</strong> · Время работы: <strong>{hours}ч {minutes}м {seconds}с</strong>
        </Typography>

        {/* Основные счётчики */}
        <Box sx={{ my: 6 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#10B981">
                  {stats.total_comments_sent}
                </Typography>
                <Typography color="text.secondary">Отправлено</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#8B5CF6">
                  {stats.total_messages_received}
                </Typography>
                <Typography color="text.secondary">Обнаружено</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#F59E0B">
                  {stats.total_messages_filtered}
                </Typography>
                <Typography color="text.secondary">Отфильтровано</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#DC2626">
                  {stats.total_comments_failed}
                </Typography>
                <Typography color="text.secondary">Ошибок</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* По каналам */}
        {Object.keys(stats.channel_stats).length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Typography variant="h6" color="#1E40AF" fontWeight={600} gutterBottom>
              По каналам
            </Typography>
            <TableContainer component={Paper} elevation={2}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Канал</strong></TableCell>
                    <TableCell align="right"><strong>Обнаружено</strong></TableCell>
                    <TableCell align="right"><strong>Прокомментировано</strong></TableCell>
                    <TableCell align="right"><strong>Фильтр</strong></TableCell>
                    <TableCell align="right"><strong>Ошибки</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(stats.channel_stats).map(([channel, s]) => (
                    <TableRow key={channel}>
                      <TableCell>{channel}</TableCell>
                      <TableCell align="right">{s.received}</TableCell>
                      <TableCell align="right" sx={{ color: '#10B981', fontWeight: 600 }}>{s.commented}</TableCell>
                      <TableCell align="right">{s.filtered}</TableCell>
                      <TableCell align="right" sx={{ color: s.failed > 0 ? '#DC2626' : 'inherit' }}>
                        {s.failed}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* По аккаунтам */}
        {Object.keys(stats.account_stats).length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Typography variant="h6" color="#1E40AF" fontWeight={600} gutterBottom>
              По аккаунтам
            </Typography>
            <TableContainer component={Paper} elevation={2}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Аккаунт</strong></TableCell>
                    <TableCell align="right"><strong>Комментариев</strong></TableCell>
                    <TableCell align="right"><strong>Ошибок</strong></TableCell>
                    <TableCell align="right"><strong>Блокировок</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(stats.account_stats).map(([acc, s]) => (
                    <TableRow key={acc}>
                      <TableCell>{acc}</TableCell>
                      <TableCell align="right" sx={{ color: '#10B981' }}>{s.comments_sent}</TableCell>
                      <TableCell align="right" sx={{ color: s.failed > 0 ? '#DC2626' : 'inherit' }}>
                        {s.failed}
                      </TableCell>
                      <TableCell align="right" sx={{ color: (s.blocked_count || 0) > 0 ? '#DC2626' : 'inherit', fontWeight: 'bold' }}>
                        {s.blocked_count || 0}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Последний комментарий — САМОЕ ГЛАВНОЕ */}
        {lastComment && (
          <Box sx={{ mb: 6, p: 5, bgcolor: '#F0FDF4', borderRadius: 3, border: '2px solid #86EFAC' }}>
            <Typography variant="h6" color="#166534" gutterBottom>
              Последний комментарий
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Chip label={lastComment.account} color="primary" size="small" sx={{ mr: 1 }} />
              <Chip label={lastComment.channel} color="secondary" size="small" />
            </Box>

            {/* Оригинальный пост */}
            <Box sx={{ mb: 3, p: 3, bgcolor: '#FEF3C7', borderRadius: 2, border: '1px dashed #F59E0B' }}>
              <Typography fontWeight={600} fontSize="0.95rem" color="#92400E" gutterBottom>
                Оригинальный пост:
              </Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap', color: '#78350F' }}>
                {lastComment.original_text}
              </Typography>
            </Box>

            {/* Наш комментарий */}
            <Box sx={{ p: 3, bgcolor: 'white', borderRadius: 2, border: '1px solid #BBF7D0' }}>
              <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: '1.1rem', lineHeight: 1.7, color: '#166534' }}>
                {lastComment.comment_text}
              </Typography>
            </Box>

            <Typography fontSize="0.9rem" color="text.secondary" mt={2}>
              {new Date(lastComment.timestamp).toLocaleString('ru-RU')}
            </Typography>
          </Box>
        )}

        {/* Ошибка */}
        {stats.last_error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            <strong>Последняя ошибка:</strong> {stats.last_error.message}
            {stats.last_error.account && ` (аккаунт: ${stats.last_error.account})`}
            {stats.last_error.channel && ` (канал: ${stats.last_error.channel})`}
            <br />
            <Typography component="span" fontSize="0.85rem">
              {new Date(stats.last_error.timestamp).toLocaleString('ru-RU')}
            </Typography>
          </Alert>
        )}

        {/* Инфо */}
        <Grid container spacing={3} sx={{ mt: 2, color: '#4B5563' }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <strong>Запущен:</strong> {new Date(stats.started_at).toLocaleString('ru-RU')}
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <strong>Последняя активность:</strong>{' '}
            {stats.last_activity_at ? new Date(stats.last_activity_at).toLocaleString('ru-RU') : '—'}
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default NeuroCommentingDetail;